import { Building2, ChevronsUpDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { apiErrorStatusFallbackPresets, resolveApiErrorMessage } from '@/lib/api-error-translations';
import { cn } from '@/lib/utils';
import { useOrganizations } from '@/features/organizations/hooks/useOrganizations';
import { useCurrentOrganizationStore } from '@/features/organizations/store/useCurrentOrganizationStore';
import {
    getVisibleOrganizations,
    ORGANIZATION_MENU_PAGE_SIZE
} from '@/features/organizations/components/organization-menu-pagination';

function resolveOrganizationSwitcherError(
    error: unknown,
    t: (key: string, options?: Record<string, unknown>) => string
): string {
    return resolveApiErrorMessage(error, t, {
        fallbackKey: 'apiErrors.organizations.loadFailed',
        statusFallbacks: apiErrorStatusFallbackPresets.organizationsLoad
    });
}

export function OnboardingOrganizationSwitcher() {
    const { t } = useTranslation();
    const organizationsQuery = useOrganizations();
    const currentOrganizationId = useCurrentOrganizationStore((state) => state.currentOrganizationId);
    const setCurrentOrganizationId = useCurrentOrganizationStore((state) => state.setCurrentOrganizationId);
    const [visibleCount, setVisibleCount] = useState(5);

    useEffect(() => {
        if (!organizationsQuery.data) {
            return;
        }

        if (organizationsQuery.data.length === 0) {
            if (currentOrganizationId !== null) {
                setCurrentOrganizationId(null);
            }
            return;
        }

        const persistedOrganizationExists = organizationsQuery.data.some(
            (organization) => organization.id === currentOrganizationId
        );

        if (!persistedOrganizationExists) {
            setCurrentOrganizationId(organizationsQuery.data[0].id);
        }
    }, [currentOrganizationId, organizationsQuery.data, setCurrentOrganizationId]);

    const currentOrganization =
        organizationsQuery.data?.find((organization) => organization.id === currentOrganizationId) ?? null;
    const visibleOrganizations = useMemo(
        () =>
            getVisibleOrganizations({
                organizations: organizationsQuery.data ?? [],
                currentOrganizationId,
                visibleCount
            }),
        [currentOrganizationId, organizationsQuery.data, visibleCount]
    );
    const hasMoreOrganizations = (organizationsQuery.data?.length ?? 0) > visibleOrganizations.length;

    if (organizationsQuery.isPending) {
        return <Skeleton className='h-9 w-40' />;
    }

    if (organizationsQuery.isError) {
        return (
            <div className='rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive'>
                {resolveOrganizationSwitcherError(organizationsQuery.error, t)}
            </div>
        );
    }

    if (!organizationsQuery.data?.length) {
        return (
            <div className='space-y-1 rounded-lg border border-dashed p-3'>
                <p className='text-sm font-medium'>{t('organizations.switcher.emptyTitle')}</p>
                <p className='text-xs text-muted-foreground'>{t('organizations.switcher.emptyDescription')}</p>
            </div>
        );
    }

    return (
        <DropdownMenu onOpenChange={(open) => !open && setVisibleCount(ORGANIZATION_MENU_PAGE_SIZE)}>
            <DropdownMenuTrigger asChild>
                <Button variant='selector' size='selector' className='w-auto max-w-[180px] justify-between'>
                    <div className='flex min-w-0 items-center gap-2 text-left'>
                        <div className='flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-primary/10 text-primary'>
                            <Building2 className='h-3.5 w-3.5' />
                        </div>
                        <div className='min-w-0'>
                            <div className='truncate text-sm font-medium'>
                                {currentOrganization?.name ?? t('organizations.switcher.selectOrganization')}
                            </div>
                        </div>
                    </div>
                    <ChevronsUpDown className='h-3.5 w-3.5 shrink-0 text-visible-icon' />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='w-[var(--radix-dropdown-menu-trigger-width)] min-w-72' align='start'>
                <DropdownMenuLabel>{t('organizations.switcher.label')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className='max-h-[400px] overflow-y-auto pr-2'>
                    {visibleOrganizations.map((organization) => {
                        const isSelected = organization.id === currentOrganizationId;

                        return (
                            <DropdownMenuItem
                                key={organization.id}
                                density='roomy'
                                className='items-start'
                                onSelect={() => setCurrentOrganizationId(organization.id)}
                            >
                                <div
                                    className={cn(
                                        'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-muted/40 text-muted-foreground dark:text-foreground',
                                        isSelected &&
                                            'border-primary/30 bg-primary/10 text-primary dark:text-primary-foreground'
                                    )}
                                >
                                    <Building2 className='h-4 w-4' />
                                </div>
                                <div className='min-w-0 flex-1'>
                                    <div className='truncate text-sm font-medium'>{organization.name}</div>
                                    <div className='mt-1 flex items-center gap-2'>
                                        <Badge variant='outline' appearance='role'>
                                            {t(`organizationMembers.roles.${organization.membership.role}`)}
                                        </Badge>
                                    </div>
                                </div>
                            </DropdownMenuItem>
                        );
                    })}
                </div>
                {hasMoreOrganizations ? (
                    <>
                        <DropdownMenuSeparator />
                        <div className='px-2 py-2'>
                            <Button
                                type='button'
                                variant='outline'
                                className='mx-auto flex w-auto min-w-32 justify-center'
                                onClick={() =>
                                    setVisibleCount((currentCount) => currentCount + ORGANIZATION_MENU_PAGE_SIZE)
                                }
                            >
                                Load more
                            </Button>
                        </div>
                    </>
                ) : null}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
